<template>
  <v-card elevation="0" flat>
    <v-card-text class="pa-0 pt-2">
      <h3 class="font-weight-bold black--text mb-4">{{ $t('oma.history') }}</h3>

      <div class="d-flex align-center justify-space-between flex-wrap mb-4">
        <v-text-field
          v-model="search"
          :label="$t('search')"
          outlined
          dense
          hide-details
          clearable
          prepend-inner-icon="mdi-magnify"
          style="max-width: 360px"
          @input="onSearch"
          @click:clear="onSearch"
        ></v-text-field>

        <div class="d-flex align-center">
          <span class="text--secondary mr-3">{{ rangeEnd }} {{ $t('of') }} {{ totalCount }}</span>
          <v-btn
            icon
            outlined
            color="kenyaGreen"
            small
            class="mr-2"
            :disabled="loading || page <= 1"
            @click="prevPage"
          >
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>
          <v-btn
            icon
            outlined
            color="kenyaGreen"
            small
            :disabled="loading || page >= totalPages"
            @click="nextPage"
          >
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>
        </div>
      </div>

      <v-data-table
        :headers="headers"
        :items="items"
        :loading="loading"
        :loading-text="$t('loadingData')"
        hide-default-footer
        :items-per-page="itemsPerPage"
        :server-items-length="totalCount"
        item-key="id"
        class="bulk-history-table"
      >
        <template v-slot:item.originalFileName="{ item }">
          {{ item.originalFileName || '—' }}
        </template>

        <template v-slot:item.createdAt="{ item }">
          {{ formatDate(item.createdAt) }}
        </template>

        <template v-slot:item.status="{ item }">
          <v-chip small outlined :color="statusColor(item.status)" class="bulk-history-status">
            {{ statusLabel(item.status) }}
          </v-chip>
        </template>

        <template v-slot:item.action="{ item }">
          <v-btn
            v-if="item.downloadUrl"
            text
            small
            color="info"
            class="text-capitalize px-0"
            :href="item.downloadUrl"
            target="_blank"
            rel="noopener"
            download
          >
            {{ $t('download') }}
          </v-btn>
          <span v-else>—</span>
        </template>

        <template v-slot:no-data>
          <div class="py-6 text--secondary">
            {{ $t('memberDataSection.greenCoffee.bulkImport.historyEmpty') }}
          </div>
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>
</template>

<script>
import moment from "moment";
import GreenCoffeeBulkImportService from "@/_services/GreenCoffeeBulkImportService";
import { SERVER_BULK_IMPORT_STATUS } from "@/utils/greenCoffeeBulkImport";

export default {
  name: "GreenCoffeeBulkImportHistory",
  data() {
    return {
      loading: false,
      search: "",
      items: [],
      totalCount: 0,
      page: 1,
      itemsPerPage: 10,
      searchTimer: null,
      headers: [
        {
          text: this.$t("memberDataSection.greenCoffee.bulkImport.historyFile"),
          value: "originalFileName",
        },
        {
          text: this.$t("memberDataSection.greenCoffee.bulkImport.historyImportedOn"),
          value: "createdAt",
        },
        { text: this.$t("oma.status"), value: "status" },
        { text: this.$t("oma.action"), value: "action", sortable: false },
      ],
    };
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.totalCount / this.itemsPerPage));
    },
    rangeEnd() {
      return Math.min(this.page * this.itemsPerPage, this.totalCount);
    },
  },
  watch: {
    page() {
      this.fetchHistory();
    },
  },
  mounted() {
    this.fetchHistory();
  },
  methods: {
    async fetchHistory() {
      this.loading = true;
      try {
        const res = await GreenCoffeeBulkImportService.getImportHistory({
          page: this.page,
          itemsPerPage: this.itemsPerPage,
          search: this.search || undefined,
        });
        if (res && res.success !== false && res.data) {
          this.items = res.data.rows || [];
          this.totalCount = res.data.count || 0;
        } else {
          this.items = [];
          this.totalCount = 0;
          if (res && res.message) this.$notify({ type: "error", text: res.message });
        }
      } catch (err) {
        this.items = [];
        this.totalCount = 0;
        this.$notify({
          type: "error",
          text: err?.message || this.$t("memberDataSection.greenCoffee.bulkImport.genericError"),
        });
      } finally {
        this.loading = false;
      }
    },
    onSearch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        if (this.page !== 1) this.page = 1; // watcher fetches
        else this.fetchHistory();
      }, 500);
    },
    prevPage() {
      if (this.page > 1) this.page -= 1;
    },
    nextPage() {
      if (this.page < this.totalPages) this.page += 1;
    },
    statusLabel(status) {
      const map = {
        [SERVER_BULK_IMPORT_STATUS.VALIDATING]: "statusValidating",
        [SERVER_BULK_IMPORT_STATUS.SUCCESS]: "statusSuccess",
        [SERVER_BULK_IMPORT_STATUS.CANCELLED]: "statusCancelled",
      };
      const key = map[status];
      return key ? this.$t(`memberDataSection.greenCoffee.bulkImport.${key}`) : status || "—";
    },
    statusColor(status) {
      switch (status) {
        case SERVER_BULK_IMPORT_STATUS.SUCCESS:
          return "kenyaGreen";
        case SERVER_BULK_IMPORT_STATUS.CANCELLED:
          return "third";
        case SERVER_BULK_IMPORT_STATUS.VALIDATING:
          return "gray";
        default:
          return "gray";
      }
    },
    formatDate(value) {
      return value ? moment(value).format("DD-MMM-YYYY").toUpperCase() : "—";
    },
  },
};
</script>

<style lang="scss" scoped>
.bulk-history-status {
  min-width: 110px;
  justify-content: center;
  font-weight: 500;
}

.bulk-history-table ::v-deep tbody tr:nth-of-type(even) {
  background-color: #f3f3f3;
}

.bulk-history-table ::v-deep thead th span {
  font-weight: 600;
}
</style>
